/**
 * The cumulative board: one row per target identity, across every run.
 *
 * The counterpart of `TargetTable`, which shows what ONE run concluded. Four
 * differences are deliberate rather than cosmetic:
 *
 * * The corroboration column is headed **Runs** and never "confirmations". Runs
 *   of this harness share a knowledge graph, a rubric and a model, so their
 *   agreement is not independent replication, and a column header is exactly the
 *   place that distinction gets lost. The API's own caveat is rendered above the
 *   table, verbatim, instead of being paraphrased here.
 * * Both `best_*` and `latest_*` are shown. Where they disagree, two runs reached
 *   different conclusions about the same target — the most useful thing on the
 *   row, and invisible if only one of them were displayed.
 * * **No versus framing, anywhere on this tab.** Two board rows can come from two
 *   different sweeps, so their Elo and BTL values were never compared with each
 *   other; a "leads by" figure would be a duel between numbers from different
 *   pools. The comparison the data supports is each rating against the 1500 every
 *   tournament starts from, which is what `EloStrip` and `EloRange` draw. Do not
 *   add a versus band here — it is correct on *This run* only, where rank 1 and
 *   rank 2 genuinely played each other in one tournament.
 * * **No axis spine.** Board rows carry no `axis_scores` — the store projects
 *   `latest_coverage` / `best_coverage` and nothing per-axis — so the score cell is
 *   a `ScoreMeter` and the drawer says why the breakdown is absent at this level. A
 *   spine assembled from a coverage figure would be a fabricated split.
 * * The score cell holds up to **three** figures, and they are three different
 *   claims. `latest`/`best` are what runs measured. The third, from
 *   `infra/sql/022_champion_score.sql`, is what a champions recheck arrived at from
 *   the stored record — labelled `re-scored` where it re-weighted axes a review
 *   measured, and `re-derived` where a model was asked to reconstruct axes that were
 *   never stored. It appears in the cell only on rows no run scored, and it is never
 *   folded into either measured figure: an inference shown as a measurement is the
 *   fabrication the provenance columns exist to catch.
 *
 * The row's detail is a `Drawer` with `eyebrow="Board entry"`, not an expanded row.
 * The eyebrow exists precisely so a cross-run consolidation is never read as one
 * run's result, and the drawer leaves the rows around it in place — an expanded row
 * pushed the next target off screen, which is the one comparison a reader of a board
 * actually wants.
 *
 * The drawer can also commission a full report. That needs one thing a board entry
 * does not have: a report is scoped to `(run_id, target_id)`, so `BoardReport` below
 * offers one on the newest observation and says which run that is. Not a synthesis
 * across runs — nothing produces one of those, and a button implying otherwise would
 * be a claim.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight } from 'lucide-react'
import {
  cn,
  formatEvidenceList,
  formatLigandStarts,
  formatNumber,
  formatTimestamp,
} from '../lib/utils'
import { joinList, toCsv, type CsvColumn } from '../lib/csv'
import { downloadCsv } from '../lib/download'
import { EloRange } from './BoardLeader'
import { CompoundId } from './CompoundId'
import { OrderCompounds } from './OrderCompounds'
import type { TokenGetter } from '../api/leaderboard'
import { EvidenceLinks } from './EvidenceLinks'
import { ReportButton } from './ReportButton'
import { RetireButton } from './RetireButton'
import { TierBadge } from './StatusBadge'
import { Button } from './ui/Button'
import { Card, CardHeader } from './ui/Card'
import { Chip } from './ui/Chip'
import { Drawer, DrawerSection } from './ui/Drawer'
import { DownloadCsvButton } from './ui/DownloadCsvButton'
import { EloStrip } from './ui/EloStrip'
import { Meter, ScoreMeter } from './ui/Meter'
import type {
  BoardEntry,
  BoardObservation,
  BoardSortColumn,
  BoardTierFloor,
  SortDirection,
} from '../api/types'
import { BOARD_TIER_FLOORS } from '../api/types'
import type { BoardRetire } from '../hooks/useBoard'
import type { ReportsState } from '../hooks/useReports'

/**
 * A header cell.
 *
 * `sorts` may hold two keys, which is how `Elo` and `Tier` each became one column
 * without losing a sort the API offers: the sub-labels are the buttons. A column
 * with no `sorts` is not sortable — the API rejects anything outside
 * `BOARD_SORT_COLUMNS` with a 400, so a header must not offer to sort by coverage
 * or by the recap flag.
 */
interface Column {
  label: string
  sorts?: { key: BoardSortColumn; label: string }[]
  className?: string
  title?: string
  /** Suppressed while grouping by pathway: the group subheader carries the value. */
  pathwayColumn?: boolean
}

const COLUMNS: Column[] = [
  {
    label: 'Runs',
    sorts: [{ key: 'runs_identified', label: 'Runs' }],
    className: 'w-16 text-right',
    title:
      'Runs of this harness that ranked this target — not confirmations. They share a graph, a rubric and a model, so their agreement is correlated. The API’s caveat is printed above the table.',
  },
  {
    label: 'Target',
    sorts: [{ key: 'protein', label: 'Target' }],
    // A minimum and not a fixed width: every other column here is fixed, so without
    // one the browser gives the only flexible column whatever is left and a protein
    // name breaks across four lines beside its own chevron.
    className: 'min-w-[13rem]',
  },
  {
    label: 'Score',
    // One sub-label per line the cell can show, and `re-derived` is its own sort
    // rather than folded into the others because it is a different kind of number:
    // ordering by it lists the rows a champions recheck reached, which is the only
    // way to find them in a board of hundreds. A single `sorts` entry would replace
    // the column's name with the sub-label's, so the three keep the name above them.
    sorts: [
      { key: 'latest_score', label: 'latest' },
      { key: 'best_score', label: 'best' },
      { key: 'champion_score', label: 're-derived' },
    ],
    className: 'w-48',
    title:
      'The latest run’s rubric score with the share of the rubric its measured axes carried, and beneath it the best any run reached. Shown as a pair or not at all — 90 over a fifth of the rubric is not 90. Board rows carry no per-axis scores, so there is no axis spine at this level. A row no run scored may carry a third figure from a champions recheck, labelled “re-scored” where it was computed from measured axes and “re-derived” where a model reconstructed them; sorting is over that third figure only, since the measured pair is not sortable.',
  },
  {
    label: 'Elo',
    sorts: [
      { key: 'latest_elo', label: 'latest' },
      { key: 'best_elo', label: 'best' },
    ],
    className: 'w-24 text-right',
    title:
      'Elo from the runs that ranked this target. Unbounded, base 1500, K=24 — a number and a range, never a bar. Ratings from two runs come from two pools and are not comparable with each other; open the row for the marks against the 1500 baseline.',
  },
  {
    label: 'Tier',
    sorts: [
      { key: 'latest_tier', label: 'latest' },
      { key: 'best_tier', label: 'best' },
    ],
    className: 'w-28',
    title:
      'The ranker’s promotion band, latest and best. Where the two differ, two runs reached different conclusions about the same target — both are kept.',
  },
  {
    label: 'Recap',
    className: 'w-28',
    title:
      'What a recap found: ruled out by at least one run, examined and not ruled out, or never examined. The third is not the second — “no disqualifier” is a verdict on a measurement, and nobody made it for an unexamined row.',
  },
  {
    label: 'Pathway',
    sorts: [{ key: 'pathway', label: 'Pathway' }],
    pathwayColumn: true,
  },
  {
    label: 'Ligand',
    sorts: [{ key: 'ligand_start_count', label: 'Ligand' }],
    className: 'w-24 text-right',
    // Recorded, not scored, and the header says what it counts rather than what it
    // would be nice for it to mean: a co-crystallised ligand with an orderable
    // analog is a cheap test, not evidence that the target is a good target.
    title:
      'Co-crystallised ligands found for this target with orderable analogs in a compound catalog — a cheap starting point for a test, NOT activity, potency or validation. Sorting on it orders by what can be bought, never by target quality. Open the row for the PDB entries and catalog ids.',
  },
  {
    label: 'Champion',
    sorts: [{ key: 'champion_elo', label: 'Champion' }],
    className: 'w-24 text-right',
    // A different quantity from `best_elo`, which is why it gets its own column
    // rather than overwriting one: a per-run Elo is the property of the tournament
    // that produced it, and this one is cross-run, one pool, one rubric digest.
    title:
      'Elo and rank from the last champions tournament — a cross-run pool under one rubric digest, not a per-run Elo. Rounds entered are shown beside it. Agreement among runs of this harness, which share a graph, a rubric and a model: not independent replication.',
  },
  {
    label: 'Last seen',
    sorts: [{ key: 'last_seen_at', label: 'Last seen' }],
    className: 'w-36',
  },
]

/**
 * What each `ligand_start_source` value licenses, in the one place the board says
 * it. Mirrors `context/glossary` and `ligand_starts.LIGAND_START_SOURCES`; a value
 * this map does not know is rendered as itself rather than as an empty cell, so a
 * new source added on the Python side shows up instead of disappearing.
 */
const LIGAND_SOURCE_NOTES: Record<string, string> = {
  measured: 'Structures searched, ligands classified, catalog answered.',
  no_ligand:
    'Structures exist; none carried a molecule worth ordering an analog of. A result, not a gap.',
  no_structure:
    'No experimental structure for this protein, and no homolog structure past the identity and coverage floors. Ordinary for plant proteins (~4% of Arabidopsis has one) and not a mark against the target.',
  no_accession: 'No accession on the record to search with.',
  structure_unreachable:
    'The PDB search did not answer, or this target was past the sweep’s cap. The question is open.',
  catalog_unreachable:
    'Ligands were found but the compound catalog did not answer. The ligands stand; the orderable answer does not.',
}

/**
 * The ligand cell. Three renderings for three states, which is the whole point.
 *
 * A count — including `0`, which means the search ran and found nothing orderable
 * and is a finding. A dash *with the reason on hover* when nothing was measured. And
 * "not measured" when the row predates the column, which is neither of the other two
 * and must not read as "none found". Collapsing any of these into a plain dash is
 * the mistake the source column exists to prevent.
 */
function LigandCell({ entry }: { entry: BoardEntry }) {
  const source = entry.ligand_start_source ?? null
  const count = entry.ligand_start_count
  const starts = entry.latest_ligand_starts ?? []
  const tiers = starts.flatMap((s) => (s.catalog_hits ?? []).map((h) => h.tier))
  const best = ['exact', 'close', 'analog', 'weak'].find((t) => tiers.includes(t))
  const note = source ? (LIGAND_SOURCE_NOTES[source] ?? source) : null

  if (source == null && count == null) {
    return (
      <span
        className="text-2xs text-slate-600"
        title="This board row predates the ligand lookup. Not the same as no orderable compound existing — nothing has looked."
      >
        not measured
      </span>
    )
  }
  if (count == null) {
    return (
      <span className="text-2xs text-slate-500" title={note ?? undefined}>
        {source}
      </span>
    )
  }
  return (
    <div title={note ?? undefined}>
      <div className="font-mono tabular-nums text-slate-200">{count}</div>
      {/* A similarity band over the catalog's own number, never a confidence and
          never potency — which is why the tooltip says so wherever it appears. */}
      {best && (
        <div
          className="text-2xs text-blue-400/80"
          title={`Best catalog similarity band across the ligands: ${best}. A band over the search's similarity number — not potency, not activity, not validation.`}
        >
          {best}
        </div>
      )}
      {count === 0 && (
        <div className="text-2xs text-slate-500" title={note ?? undefined}>
          none orderable
        </div>
      )}
    </div>
  )
}

/**
 * `was_disqualified`, which is three-state, in a cell where the three states look
 * like three different things.
 *
 * `null` may not render as "no". "No mammalian concern" is a verdict on a
 * measurement nobody made, and this column is the same shape of claim: a row no
 * recap has examined has to read as unexamined, not as clean. So the unexamined
 * state borrows the hollow, dashed outline the `Meter` and the axis spine use for an
 * unmeasured value — one visual language for "nobody looked" across the whole UI.
 */
function RecapCell({ entry }: { entry: BoardEntry }) {
  const flag = entry.was_disqualified
  const runs = entry.disqualified_in_runs

  if (flag === true) {
    return (
      <span
        className="inline-flex rounded-md border border-amber-800 bg-amber-900/40 px-1.5 py-0.5 text-2xs text-amber-300"
        title={`Ruled out by at least one earlier run${
          runs?.length ? `: ${runs.join(', ')}` : ''
        }. It is back on the board because a recap re-reviewed it and something had measurably changed — the dead-end row still stands.`}
      >
        ruled out once
      </span>
    )
  }
  if (flag === false) {
    return (
      <span
        className="inline-flex rounded-md border border-slate-700 bg-slate-800/60 px-1.5 py-0.5 text-2xs text-slate-400"
        title="A recap examined this target and found no run that ruled it out. A measurement, unlike the unexamined state."
      >
        no disqualifier
      </span>
    )
  }
  return (
    <span
      className="inline-flex rounded-md border border-dashed border-slate-700 px-1.5 py-0.5 text-2xs text-slate-500"
      title="No recap has examined this target. Not the same as “examined and clean” — nothing has looked, so there is no verdict to report."
    >
      not examined
    </span>
  )
}

/**
 * What a CSV export of the cumulative board holds.
 *
 * The `runs_identified` header carries its own caveat, and that is deliberate. The
 * count is not evidence — these runs share a knowledge graph, a rubric and a model,
 * so their agreement is not independent replication — and every other place it
 * appears says so: the API attaches a note to every response, the table heads the
 * column "Runs" and prints the server's sentence above it. A CSV is read further
 * from all of that than anything else here, in a spreadsheet nobody is going to open
 * the Wiki tab beside, so the qualifier travels **in the header text**.
 * `distinct_threads` and `distinct_prompts` sit next to it for the same reason they
 * do in the API: five runs from one prompt is a weaker statement than two from two.
 *
 * `latest_*` and `best_*` are both exported wherever both exist, as the table shows
 * both — where they disagree, two runs reached different conclusions about the same
 * target, and exporting one of them would silently pick a winner.
 */
const CSV_COLUMNS: CsvColumn<BoardEntry>[] = [
  { header: 'target_key', value: (e) => e.target_key },
  { header: 'protein', value: (e) => e.protein },
  { header: 'accession', value: (e) => e.accession },
  { header: 'moa', value: (e) => e.moa },
  { header: 'pathway', value: (e) => e.pathway },
  {
    header: 'runs_identified (runs of this harness, not independent confirmations)',
    value: (e) => e.runs_identified,
  },
  { header: 'distinct_threads', value: (e) => e.distinct_threads },
  { header: 'distinct_prompts', value: (e) => e.distinct_prompts },
  { header: 'best_elo', value: (e) => e.best_elo },
  { header: 'latest_elo', value: (e) => e.latest_elo },
  { header: 'worst_elo', value: (e) => e.worst_elo },
  { header: 'best_btl', value: (e) => e.best_btl },
  { header: 'latest_btl', value: (e) => e.latest_btl },
  // Score and coverage always as a pair, per infra/sql/012 — a score exported
  // without the share of the rubric behind it reads as a stronger result than it is.
  { header: 'best_score', value: (e) => e.best_score },
  { header: 'latest_score', value: (e) => e.latest_score },
  { header: 'best_coverage', value: (e) => e.best_coverage },
  { header: 'latest_coverage', value: (e) => e.latest_coverage },
  { header: 'best_tier', value: (e) => e.best_tier },
  { header: 'latest_tier', value: (e) => e.latest_tier },
  { header: 'best_rank', value: (e) => e.best_rank },
  { header: 'latest_rank', value: (e) => e.latest_rank },
  // The champions projection. `champions_rounds` carries its qualifier in the
  // header for the same reason `runs_identified` does, and one more: it counts
  // ENTRIES, so a target that placed last in three tournaments exports a 3.
  {
    header:
      'champions_rounds (tournaments entered, not won — runs of this harness, not confirmations)',
    value: (e) => e.champions_rounds,
  },
  { header: 'champion_elo', value: (e) => e.champion_elo },
  { header: 'champion_rank', value: (e) => e.champion_rank },
  { header: 'champion_session', value: (e) => e.champion_session },
  // Beside `latest_rationale`, never substituted for it: one is the wording the
  // ranking used, the other is what a champions comparison concluded, and a reader
  // needs to see that they differ.
  { header: 'champion_rationale', value: (e) => e.champion_rationale },
  // The recheck's score (infra/sql/022), and the source is not optional beside it:
  // a spreadsheet is the one place this column gets sorted next to `best_score`
  // with nothing on screen to say they are different kinds of number. `rederived`
  // means a model reconstructed the axes from the stored rationale; `run` means the
  // recheck re-weighted axes a review measured. The header carries the warning
  // because a CSV keeps no tooltips.
  {
    header: 'champion_score (read champion_score_source — `rederived` is inferred, not measured)',
    value: (e) => e.champion_score,
  },
  { header: 'champion_coverage', value: (e) => e.champion_coverage },
  { header: 'champion_score_source', value: (e) => e.champion_score_source },
  { header: 'champion_score_session', value: (e) => e.champion_score_session },
  // Three-state, and the empty cell is the third state: blank means no recap has
  // examined this target, FALSE means one did and found no run that ruled it out.
  // Exporting blank as FALSE would turn "nobody looked" into a clean bill of
  // health — which is the whole reason the column is nullable.
  {
    header: 'was_disqualified (blank = no recap examined it)',
    value: (e) => (e.was_disqualified == null ? null : e.was_disqualified),
  },
  { header: 'disqualified_in_runs', value: (e) => joinList(e.disqualified_in_runs) },
  // Recorded, not scored, and the header says so because a spreadsheet is the one
  // place a column gets sorted next to `best_score` with nothing to say they are
  // different kinds of number. The source ships beside the count for the reason the
  // API keeps them together: a blank count means the search did not run, and only
  // the source says why. `0` means it ran and found nothing orderable.
  {
    header: 'ligand_start_count (orderable analogs found — recorded, not scored)',
    value: (e) => e.ligand_start_count,
  },
  {
    header: 'ligand_start_source (blank count = not measured; see this column for why)',
    value: (e) => e.ligand_start_source,
  },
  { header: 'ligand_starts_checked_at', value: (e) => e.ligand_starts_checked_at },
  // One cell per catalog hit, flattened, because that is what a chemist filters and
  // pastes into an order. Same helper as the per-run export — see its docstring for
  // why `homolog` rides inside the PDB token instead of getting a column.
  {
    header: 'ligand_starts (pdb[provenance] comp_id source:catalog_id@similarity)',
    value: (e) => joinList(formatLigandStarts(e.latest_ligand_starts)),
  },
  // Every spelling any run used. The key is what they agreed on, not what any one
  // of them wrote, so a reader searching for their own name for the protein needs
  // this column to find the row at all.
  { header: 'aliases', value: (e) => joinList(e.aliases) },
  { header: 'evidence', value: (e) => joinList(formatEvidenceList(e.evidence)) },
  { header: 'latest_rationale', value: (e) => e.rationale },
  { header: 'first_seen_run', value: (e) => e.first_seen_run },
  { header: 'first_seen_at', value: (e) => e.first_seen_at },
  { header: 'last_seen_run', value: (e) => e.last_seen_run },
  { header: 'last_seen_at', value: (e) => e.last_seen_at },
  // The audit trail behind the count. A consolidated number that cannot be traced
  // to the rows behind it is not evidence of anything.
  { header: 'run_ids', value: (e) => joinList(e.run_ids) },
  { header: 'updated_at', value: (e) => e.updated_at },
]

/**
 * The bucket key for rows with no pathway.
 *
 * A control character rather than a word: `pathway` is free text the model writes,
 * so `"none"`, `"unknown"` and `""` are all values it could plausibly produce, and a
 * sentinel that a real value can equal would silently merge "no pathway recorded"
 * into a named group. Never rendered — the subheader reads the row's own `pathway`.
 */
const NO_PATHWAY = '\u0000no-pathway'

/** How the rows on this page are arranged. Presentation only; see `groupRows`. */
type GroupBy = 'none' | 'pathway' | 'protein'

/**
 * The rows one protein's collapsed line stands for.
 *
 * `runs` is the size of the **union** of the members' `run_ids`, never the sum: one
 * run that proposed BIO2 under two mechanisms wrote two board rows, and adding their
 * counts would report one run as two — the same double-count the cumulative board
 * exists to avoid. `exact` is false when a member carried no `run_ids` at all, in
 * which case `runs` is the largest single count and is a floor, not the answer.
 */
type ProteinGroup = {
  members: BoardEntry[]
  runs: number
  exact: boolean
}

/** Rows on the page in reading order: pathway subheaders interleaved with entries. */
type Row =
  | { kind: 'group'; pathway: string | null; count: number; key: string }
  | {
      kind: 'entry'
      entry: BoardEntry
      /** Set on the one line standing for a protein with more than one mechanism. */
      group?: ProteinGroup
      /** Set on a member row shown under an expanded collapsed line. */
      nested?: boolean
    }

/**
 * Group the rows on this page by pathway, preserving the server's order.
 *
 * Presentation only, and the copy says so: the sort still decides which rows are on
 * the page and the order inside each group, and grouping cannot reach the rows on
 * page two. A client-side regroup that looked like a whole-board grouping would be
 * the worst of both.
 *
 * A `null` pathway gets its own group labelled "No pathway recorded" rather than
 * being swept into an "Other" bucket with the rows whose pathway is simply
 * uncommon — the absence is the finding.
 */
function groupRows(
  entries: BoardEntry[],
  groupBy: GroupBy,
  expanded: Set<string>,
): Row[] {
  if (groupBy === 'protein') return proteinRows(entries, expanded)
  if (groupBy === 'none') return entries.map((entry) => ({ kind: 'entry', entry }))
  const groups = new Map<string, BoardEntry[]>()
  for (const entry of entries) {
    // A key that cannot collide with a pathway string: the model writes pathway
    // free-text, so "none" and "" are both values it could plausibly write.
    const key = entry.pathway ?? NO_PATHWAY
    const bucket = groups.get(key)
    if (bucket) bucket.push(entry)
    else groups.set(key, [entry])
  }
  return [...groups.entries()].flatMap(([key, rows]): Row[] => [
    {
      kind: 'group',
      pathway: rows[0].pathway,
      count: rows.length,
      key,
    },
    ...rows.map((entry): Row => ({ kind: 'entry', entry })),
  ])
}

/**
 * Collapse the rows on this page that share a protein into one line each.
 *
 * The board's row is `(canonical protein, canonical mechanism)` and that split is
 * deliberate — ACCase via the CT domain and ACCase via the BC domain are two
 * targets, not one row to merge. But the split also fires on two *wordings* of one
 * mechanism: BIO2 sits on the board twice as `biotin-synthase` and
 * `biotin-inhibit-mitochondrial-synthase`, and no token matching can converge those.
 * So the reader is offered the protein-level view instead of the store being made to
 * guess, and the mechanisms stay visible one click away.
 *
 * A protein with one row renders as an ordinary row: a collapse affordance over a
 * group of one is chrome that says nothing.
 *
 * The head line carries the **union** run count and the first member's own figures —
 * the first under the active sort, so sorting by score puts the group's strongest
 * mechanism on the line. It does not carry maxima taken across members: `best_score`
 * and `best_coverage` are already independent maxima from possibly different runs
 * (`infra/sql/012`), and mixing two mechanisms' numbers into one row would invent an
 * observation nobody made. The runs cell says whose figures these are, and the
 * `/Np` prompt marker is suppressed, because it describes one mechanism's history
 * and this line is not one mechanism.
 */
function proteinRows(entries: BoardEntry[], expanded: Set<string>): Row[] {
  const groups = new Map<string, BoardEntry[]>()
  for (const entry of entries) {
    // `protein_key` is the store's canonical form and is what `board` is keyed on.
    // Falling back to `target_key` rather than to the display name: a row with no
    // canonical key must group with nothing rather than with everything like it.
    const key = entry.protein_key || entry.target_key
    const bucket = groups.get(key)
    if (bucket) bucket.push(entry)
    else groups.set(key, [entry])
  }
  return [...groups.values()].flatMap((members): Row[] => {
    if (members.length === 1) return [{ kind: 'entry', entry: members[0] }]
    const runIds = new Set(members.flatMap((m) => m.run_ids ?? []))
    const exact = members.every((m) => (m.run_ids?.length ?? 0) > 0)
    const group: ProteinGroup = {
      members,
      // A floor when the ids are not all there — never a sum, and never zero: a
      // board row exists because some run wrote it.
      runs: exact
        ? runIds.size
        : Math.max(...members.map((m) => m.runs_identified)),
      exact,
    }
    const head: Row = { kind: 'entry', entry: members[0], group }
    if (!expanded.has(members[0].target_key)) return [head]
    return [
      head,
      ...members.map((entry): Row => ({ kind: 'entry', entry, nested: true })),
    ]
  })
}

interface Props {
  entries: BoardEntry[]
  /** Rows on the whole board, for the card's title. Not `entries.length`. */
  total: number
  sort: BoardSortColumn
  direction: SortDirection
  note: string | null
  /**
   * The API's champions caveat. Shown only when a row on this page carries a
   * champions figure — either a tournament entered or a rechecked score — since a
   * caveat about columns that are empty everywhere reads as a warning about the
   * numbers beside them.
   */
  championsNote: string | null
  history: Record<string, BoardObservation[]>
  /** The `min_runs` filter's current value, and the setter that refetches. */
  minRuns: number
  onMinRunsChange: (n: number) => void
  /** The tier floor, or `null` for every tier. Same shape as `minRuns`. */
  tier: BoardTierFloor | null
  onTierChange: (tier: BoardTierFloor | null) => void
  onSort: (column: BoardSortColumn) => void
  /** Called when a row's drawer opens; the observation log is fetched on demand. */
  onExpand: (targetKey: string) => void
  /**
   * Report state, shared with the per-run table. A board row can commission a
   * report even though it is not itself a run's row: the newest observation names
   * the `(run_id, target_id)` to assess, and `reports.track` puts that run in the
   * hook's polling set.
   */
  reports: ReportsState
  /**
   * Retiring a board row, or one run's whole contribution. The board's own state
   * slice, so the table does not hold a second copy of what is in flight.
   */
  retire: BoardRetire
  /** The signed-in address, shown in the confirmation. Empty when auth is off. */
  accountEmail: string
  /** For the drawer's compound-order box, which talks to the API itself. */
  getToken: TokenGetter
}

export function BoardTable({
  entries,
  total,
  sort,
  direction,
  note,
  championsNote,
  history,
  minRuns,
  onMinRunsChange,
  tier,
  onTierChange,
  onSort,
  onExpand,
  reports,
  retire,
  accountEmail,
  getToken,
}: Props) {
  // Which row's drawer is open, by `target_key` — a board row is a consolidation
  // and has no per-run id. One at a time, unlike the expanded rows this replaced:
  // a drawer is modal, so a set of open keys could not be rendered anyway.
  const [openKey, setOpenKey] = useState<string | null>(null)
  // One arrangement at a time, not two independent toggles: pathway grouping files
  // rows under subheaders and protein grouping collapses them into one line, so both
  // at once would be a subheadered list of lines that are no longer rows.
  const [groupBy, setGroupBy] = useState<GroupBy>('none')
  // Which collapsed protein lines are expanded, by the head row's `target_key`.
  const [openProteins, setOpenProteins] = useState<Set<string>>(new Set())
  const grouped = groupBy === 'pathway'

  const toggleGroupBy = (next: GroupBy) =>
    setGroupBy((prev) => (prev === next ? 'none' : next))

  const toggleProtein = (key: string) =>
    setOpenProteins((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  // Which rows a CSV export will hold. Keyed by `target_key` for the same reason.
  // Local to this table, so a selection here cannot change what the per-run table
  // exports.
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const toggleSelected = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const open = (key: string) => {
    setOpenKey(key)
    onExpand(key)
  }

  // Prune to the rows on screen whenever `entries` changes — a poll, a re-sort, a
  // `min_runs` or tier change, or a page turn. A selection that outlives its rows
  // leaves the button counting twelve while the export, built from those rows, holds
  // fewer, with nothing to say which number is true. `prev` is returned unchanged
  // when nothing was pruned, or the new `Set` would retrigger this effect forever.
  // The open drawer is closed by the same rule: a panel describing a row that is no
  // longer in the table is a detail view of nothing.
  useEffect(() => {
    const live = new Set(entries.map((e) => e.target_key))
    setSelected((prev) => {
      if (prev.size === 0) return prev
      const next = new Set([...prev].filter((key) => live.has(key)))
      return next.size === prev.size ? prev : next
    })
    setOpenKey((prev) => (prev !== null && !live.has(prev) ? null : prev))
  }, [entries])

  // The one orchestrated load: rows stagger in on **first paint only**. A ref, not
  // state, because a re-sort and a page turn must not replay it — 100 rows lifting
  // 4px every time a header is clicked is motion sickness, not polish. Read during
  // render and cleared by an effect, so only the first committed render animates.
  const firstPaint = useRef(true)
  useEffect(() => {
    firstPaint.current = false
  }, [])
  const stagger = firstPaint.current

  const selectedRows = entries.filter((e) => selected.has(e.target_key))
  const allSelected = entries.length > 0 && selectedRows.length === entries.length
  const columns = COLUMNS.filter((c) => !(grouped && c.pathwayColumn))
  const rows = useMemo(
    () => groupRows(entries, groupBy, openProteins),
    [entries, groupBy, openProteins],
  )
  const openEntry = entries.find((e) => e.target_key === openKey) ?? null

  const download = () => {
    // Everything when nothing is selected, per the button's own label.
    const rows = selectedRows.length > 0 ? selectedRows : entries
    // Dated, unlike the per-run export: the board is cumulative and changes as
    // sweeps finish, so two files a week apart are two different boards and a name
    // that does not say when it was taken makes them indistinguishable.
    downloadCsv(
      `board-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(rows, CSV_COLUMNS),
    )
  }

  let staggerIndex = -1

  return (
    <>
      <Card>
        <CardHeader
          eyebrow="Cumulative board"
          title={`${total} target${total === 1 ? '' : 's'} across every run`}
          description="One row per protein and mechanism, derived from every run's ranking and rebuilt on boot. Two rows can come from two different sweeps, so the figures below are each run's own — nothing here is a head-to-head."
          actions={
            <>
              {/* Filters server-side, so it refetches and returns to page 1. The
                  label says "runs", never "confirmations", for the same reason the
                  column does. */}
              <label className="flex items-center gap-1.5 text-2xs text-slate-400">
                <span>Runs ≥</span>
                <select
                  value={minRuns}
                  onChange={(e) => onMinRunsChange(Number(e.target.value))}
                  title="Show only targets that this many runs of the harness identified. A filter on how often a target came up, not on how strong the evidence for it is."
                  className="rounded border border-slate-700 bg-slate-900 px-1.5 py-1 font-mono text-slate-200"
                >
                  {[1, 2, 3, 5].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              {/* A floor, like "Runs ≥" beside it, because the bands are ordered
                  and "conditional or stronger" is how a board of hundreds is
                  read. Server-side for the same reason: a client-side filter
                  could only narrow the current page, which would read as a
                  filter over the whole board and silently is not — the same trap
                  "Group by pathway" names in its own tooltip.

                  Only the current vocabulary is offered. The legacy A/B/C/D rows
                  still on the board are matched by the band they mean, in
                  `store.TIER_ALIASES`, rather than as four more options here. */}
              <label className="flex items-center gap-1.5 text-2xs text-slate-400">
                <span>Tier ≥</span>
                <select
                  value={tier ?? ''}
                  onChange={(e) =>
                    onTierChange(
                      e.target.value === ''
                        ? null
                        : (e.target.value as BoardTierFloor),
                    )
                  }
                  title="Show only targets in this tier or a stronger one. A row passes on either its latest or its best tier, so the rows where two runs disagreed stay visible — the cell shows both. Rows no run has tiered are excluded by any floor: “not scored” is not a band."
                  className="rounded border border-slate-700 bg-slate-900 px-1.5 py-1 text-slate-200"
                >
                  {/* The default, and worded as the absence of a filter rather
                      than as a tier — "all" is not a band, and an empty value is
                      what keeps `tier` off the query string entirely. */}
                  <option value="">any</option>
                  {BOARD_TIER_FLOORS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                variant={grouped ? 'primary' : 'outline'}
                size="sm"
                aria-pressed={grouped}
                onClick={() => toggleGroupBy('pathway')}
                title="Group the rows on this page under their pathway. Presentation only: the sort still decides which rows are on the page and their order within each group."
              >
                Group by pathway
              </Button>
              {/* The answer to "why is BIO2 here twice?". The board's row is
                  (protein, mechanism) on purpose — ACCase via two domains is two
                  targets — so this collapses the *view* rather than merging the
                  rows, and the mechanisms stay one click away. Same page-only
                  caveat as pathway grouping: a protein whose other mechanism is on
                  page two cannot be collapsed with it. */}
              <Button
                variant={groupBy === 'protein' ? 'primary' : 'outline'}
                size="sm"
                aria-pressed={groupBy === 'protein'}
                onClick={() => toggleGroupBy('protein')}
                title="Collapse the rows on this page that share a protein into one line, showing the number of distinct runs across all of them. Presentation only: the mechanisms are kept, one click away, and a protein whose other mechanism is on another page cannot be collapsed with it."
              >
                Group by protein
              </Button>
              <DownloadCsvButton
                selectedCount={selectedRows.length}
                rowCount={entries.length}
                noun="board entries"
                onDownload={download}
              />
            </>
          }
        />

        {/* The server's own caveats, verbatim and on screen — not paraphrased, not
            truncated, not a tooltip. `runs_identified` is a count of runs of ONE
            harness, which share a graph, a rubric and a model, so the number needs
            its qualifier every time it is read. It is printed here and again beside
            the standing above, because both show that number. */}
        <div className="space-y-1.5 border-b border-slate-800 px-5 py-3">
          {note && <p className="text-2xs leading-relaxed text-slate-400">{note}</p>}
          {/* Only when something on this page carries a champions figure. The caveat
              is the server's sentence rather than a paraphrase, for the reason the
              `runs_identified` one is: two wordings of the same qualifier drift,
              and nothing would say which one a reader saw.

              `champion_score` counts as such a figure, and the rounds test alone is
              not enough for it: the recheck runs over the whole scan while the pool is
              cut at the tier floor, so a row can carry a re-derived score with
              `champions_rounds` 0 — exactly the low-tier rows the projection exists to
              fill. Gating on rounds would drop the caveat off the page whose only
              champions numbers are the ones that most need it. */}
          {championsNote &&
            entries.some(
              (e) => (e.champions_rounds ?? 0) > 0 || e.champion_score != null,
            ) && (
            <p className="text-2xs leading-relaxed text-slate-400">{championsNote}</p>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-slate-800/60">
              <tr>
                <th className="w-8 px-2 py-2">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() =>
                      setSelected(
                        allSelected
                          ? new Set()
                          : new Set(entries.map((e) => e.target_key)),
                      )
                    }
                    // A ref callback, not a `useEffect`: `indeterminate` is a DOM
                    // property with no React prop, and the callback runs on every
                    // render, so the box cannot fall out of step with the state. The
                    // braces matter — a ref callback must not return a value.
                    ref={(el) => {
                      if (el)
                        el.indeterminate = selectedRows.length > 0 && !allSelected
                    }}
                    aria-label={
                      allSelected
                        ? 'Clear the selection'
                        : 'Select every board entry on this page'
                    }
                    className="h-3.5 w-3.5 accent-brand-500"
                  />
                </th>
                {columns.map((col) => (
                  <th
                    key={col.label}
                    title={col.title}
                    className={cn(
                      'px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-400',
                      col.className,
                    )}
                  >
                    {col.sorts ? (
                      <span
                        className={cn(
                          'inline-flex items-baseline gap-2',
                          // The label is the button when there is one sort; two
                          // sub-labels need the column's own name above them.
                          col.sorts.length > 1 && 'flex-col items-start gap-0',
                        )}
                      >
                        {col.sorts.length > 1 && <span>{col.label}</span>}
                        <span className="inline-flex items-baseline gap-1.5">
                          {col.sorts.map((s) => (
                            <button
                              key={s.key}
                              type="button"
                              onClick={() => onSort(s.key)}
                              className={cn(
                                'inline-flex items-center gap-0.5 hover:text-slate-100',
                                col.sorts!.length > 1 && 'font-sans normal-case tracking-normal',
                                sort === s.key ? 'text-slate-100' : 'text-slate-500',
                              )}
                            >
                              {s.label}
                              {sort === s.key &&
                                (direction === 'asc' ? (
                                  <ArrowUp className="h-3 w-3" />
                                ) : (
                                  <ArrowDown className="h-3 w-3" />
                                ))}
                            </button>
                          ))}
                        </span>
                      </span>
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                if (row.kind === 'group') {
                  return (
                    <tr key={`group-${row.key}`}>
                      {/* Sticky against `main`, which is the scrolling ancestor, and
                          opaque rather than translucent so rows cannot show through
                          the label they are filed under. */}
                      <th
                        colSpan={columns.length + 1}
                        scope="colgroup"
                        className="sticky top-0 z-10 border-t border-slate-800 bg-slate-900 px-3 py-2 text-left"
                      >
                        <span className="inline-flex items-baseline gap-2">
                          <Chip emphasis>
                            {row.pathway ?? 'No pathway recorded'}
                          </Chip>
                          <span className="font-mono text-2xs tabular-nums text-slate-500">
                            {row.count} on this page
                          </span>
                        </span>
                      </th>
                    </tr>
                  )
                }
                const e = row.entry
                const group = row.group
                staggerIndex += 1
                const isOpen = openKey === e.target_key
                return (
                  <tr
                    // A collapsed head and its first member are the same entry, so
                    // the key has to say which of the two rows this is.
                    key={row.nested ? `member-${e.target_key}` : e.target_key}
                    className={cn(
                      'border-t border-slate-800 hover:bg-slate-800/30',
                      isOpen && 'bg-slate-800/40',
                      row.nested && 'bg-slate-900/60',
                      stagger && 'animate-rise',
                    )}
                    // First paint only; see `firstPaint` above. 15ms per row reads
                    // as one list arriving rather than as rows queueing.
                    style={stagger ? { animationDelay: `${staggerIndex * 15}ms` } : undefined}
                  >
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        // A collapsed line stands for its members, so its box is
                        // theirs: exporting the line has to export the mechanisms it
                        // hides, or the CSV holds one of the two rows the reader was
                        // looking at.
                        checked={
                          group
                            ? group.members.every((m) => selected.has(m.target_key))
                            : selected.has(e.target_key)
                        }
                        onChange={() =>
                          group
                            ? setSelected((prev) => {
                                const next = new Set(prev)
                                const all = group.members.every((m) =>
                                  prev.has(m.target_key),
                                )
                                for (const m of group.members) {
                                  if (all) next.delete(m.target_key)
                                  else next.add(m.target_key)
                                }
                                return next
                              })
                            : toggleSelected(e.target_key)
                        }
                        // Named, not "row 4": a screen reader announcing the box has
                        // to say which target it selects, and the position alone is
                        // meaningless once the board is re-sorted.
                        aria-label={
                          group
                            ? `Select all ${group.members.length} ${e.protein} mechanisms for export`
                            : `Select ${e.protein} for export`
                        }
                        className="h-3.5 w-3.5 accent-brand-500"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="font-mono tabular-nums text-slate-100">
                        {/* The union across the group, not the sum: one run that
                            proposed this protein under two mechanisms wrote two board
                            rows, and adding their counts reports it as two runs. A
                            floor with a `≥` when a member recorded no run ids. */}
                        {group
                          ? `${group.exact ? '' : '≥'}${group.runs}`
                          : e.runs_identified}
                      </span>
                      {/* The qualifier, not decoration: N runs from one prompt is a
                          weaker statement than N runs from N prompts, and the count
                          alone cannot say which this is.

                          Suppressed on a collapsed line: it describes one mechanism's
                          history and the line is not one mechanism. */}
                      {!group && e.distinct_prompts > 1 && (
                        <span
                          className="ml-1 font-mono text-2xs text-slate-500"
                          title={`${e.distinct_prompts} distinct prompts`}
                        >
                          /{e.distinct_prompts}p
                        </span>
                      )}
                    </td>
                    <td className={cn('px-3 py-2', row.nested && 'pl-8')}>
                      <button
                        type="button"
                        // A collapsed line has no drawer of its own — it is not a
                        // board row — so its name opens the mechanisms instead.
                        onClick={() =>
                          group
                            ? toggleProtein(e.target_key)
                            : open(e.target_key)
                        }
                        aria-haspopup={group ? undefined : 'dialog'}
                        aria-expanded={group ? openProteins.has(e.target_key) : undefined}
                        className="group/row flex max-w-md items-baseline gap-1 text-left"
                      >
                        <span
                          className={cn(
                            'group-hover/row:text-brand-300',
                            row.nested
                              ? 'text-slate-400'
                              : 'font-medium text-slate-100',
                          )}
                        >
                          {e.protein}
                        </span>
                        {group && openProteins.has(e.target_key) ? (
                          <ChevronDown className="h-3.5 w-3.5 shrink-0 self-center text-slate-600 group-hover/row:text-brand-300" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 shrink-0 self-center text-slate-600 group-hover/row:text-brand-300" />
                        )}
                      </button>
                      {e.accession && (
                        <div className="font-mono text-2xs text-slate-500">
                          {e.accession}
                        </div>
                      )}
                      <div className="text-2xs text-slate-400">
                        {e.moa ?? 'No mechanism recorded'}
                      </div>
                      {/* Which mechanism the figures on this line belong to, said
                          plainly rather than left for the reader to assume they are
                          the protein's. The other rows are one click away and carry
                          their own. */}
                      {group && (
                        <button
                          type="button"
                          onClick={() => toggleProtein(e.target_key)}
                          title={`${group.exact ? '' : 'At least '}${group.runs} distinct run${
                            group.runs === 1 ? '' : 's'
                          } identified ${e.protein}, across ${group.members.length} mechanisms on this page. Every figure on this line is “${e.moa ?? 'no mechanism recorded'}”'s — the other mechanisms carry their own.`}
                          className="mt-0.5 text-2xs text-brand-300 hover:text-brand-200"
                        >
                          {openProteins.has(e.target_key)
                            ? 'hide the other mechanisms'
                            : `+${group.members.length - 1} more mechanism${
                                group.members.length === 2 ? '' : 's'
                              } · figures above are this one's`}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {/* The latest run's pair. No axis spine: board rows carry no
                          `axis_scores`, and a spine drawn from a single coverage
                          figure would be a fabricated per-axis split. */}
                      <ScoreMeter score={e.latest_score} coverage={e.latest_coverage} />
                      {/* `latest_score` is the NEWEST run's, and the newest run is
                          not necessarily the one that applied the rubric: every
                          sweep before `infra/sql/012_score_and_coverage.sql`
                          predates the column, and one after it stored a full
                          ranking with neither number. On those rows the cell above
                          says "Not scored", which a reader takes as "nobody ever
                          scored this target" — a stronger claim than the record
                          supports when an earlier run's ranking holds the number.

                          Bare, and deliberately NOT paired with `best_coverage`:
                          012 states the two are independent maxima that need not
                          come from the same run, so printing them together would
                          invent an observation. The Elo cell beside this sets the
                          same precedent, and the drawer holds each run's own pair. */}
                      {e.best_score !== null &&
                        e.best_score !== undefined &&
                        e.best_score !== e.latest_score && (
                          <div
                            className="mt-0.5 font-mono text-2xs tabular-nums text-slate-500"
                            title="The highest score any run gave this target. From a different ranking than the latest one, and its coverage is a separate maximum — so the pair as a single run measured it is in that run's ranking, not here."
                          >
                            best {formatNumber(e.best_score)}
                          </div>
                        )}
                      {/* The champions recheck's figure (`infra/sql/022`), and only
                          on the rows with no measured pair at all — which is the
                          population the projection exists for. Where a run did score
                          the target, a third number derived from that same record is
                          noise in a cell; the drawer carries it either way for a
                          reader who wants to compare them.

                          LABELLED, not merely coloured, and the label separates the
                          two sources because they are two different claims:
                          `run` means the recheck re-weighted axis scores a review
                          actually measured, and anything else means a model was
                          handed the stored rationale and asked what the axes must
                          have been. A reconstructed number rendered as a score is a
                          fabricated measurement, which is the whole of D6.

                          Its coverage IS printed with it, unlike `best` above: 022
                          takes both from one champions row, so this is one
                          observation's pair rather than two independent maxima. */}
                      {e.latest_score == null &&
                        e.best_score == null &&
                        e.champion_score != null && (
                          <div
                            className={cn(
                              'mt-0.5 font-mono text-2xs tabular-nums',
                              e.champion_score_source === 'run'
                                ? 'text-slate-400'
                                : 'text-amber-400',
                            )}
                            title={
                              e.champion_score_source === 'run'
                                ? `Re-scored by a champions session (${e.champion_score_session ?? 'session not recorded'}) from axis scores a review measured, under that session's rubric weights. Arithmetic over stored measurements — no run's ranking holds this number, because the run that measured the axes stored no score.`
                                : `Re-DERIVED by a champions session (${e.champion_score_session ?? 'session not recorded'}): no axis scores were stored, so the model was given this target's stored record and asked to reconstruct them. Inferred from the rationale by the same model that wrote it — not a measurement, and not corroboration. Treat it as a reading aid for a row nobody scored.`
                            }
                          >
                            {e.champion_score_source === 'run'
                              ? 're-scored'
                              : 're-derived'}{' '}
                            {formatNumber(e.champion_score)}
                            {e.champion_coverage != null && (
                              <span className="text-slate-500">
                                {' '}
                                · {Math.round(e.champion_coverage * 100)}% of rubric
                              </span>
                            )}
                          </div>
                        )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-mono tabular-nums text-slate-200">
                        {formatNumber(e.latest_elo)}
                      </div>
                      <div
                        className="font-mono text-2xs tabular-nums text-slate-500"
                        title="The best any run rated it. From a different tournament than the latest, so the two are not a trend."
                      >
                        best {formatNumber(e.best_elo)}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <TierBadge tier={e.latest_tier} />
                      {e.best_tier !== e.latest_tier && (
                        <div
                          className="mt-0.5 text-2xs text-slate-500"
                          title="Two runs reached different conclusions about this target. Both are kept; neither is the board's answer."
                        >
                          best: {e.best_tier ?? 'none'}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <RecapCell entry={e} />
                    </td>
                    {!grouped && (
                      <td className="px-3 py-2">
                        {e.pathway ? (
                          <Chip>{e.pathway}</Chip>
                        ) : (
                          <span className="text-2xs text-slate-600">
                            not recorded
                          </span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 text-right">
                      <LigandCell entry={e} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      {(e.champions_rounds ?? 0) > 0 ? (
                        <>
                          <div className="font-mono tabular-nums text-slate-200">
                            {formatNumber(e.champion_elo)}
                            {e.champion_rank != null && (
                              <span className="ml-1 text-2xs text-slate-500">
                                #{e.champion_rank}
                              </span>
                            )}
                          </div>
                          {/* Rounds entered, not rounds won. A target can enter every
                              tournament and place last in all of them, so a bare
                              count beside a rank must not read as a tally of wins. */}
                          <div
                            className="font-mono text-2xs text-slate-500"
                            title={`Entered ${e.champions_rounds} champions tournament${
                              e.champions_rounds === 1 ? '' : 's'
                            } — entries, not wins`}
                          >
                            {e.champions_rounds}×
                          </div>
                        </>
                      ) : (
                        /* Never entered one. Distinct from placing last, and distinct
                           from a runtime that predates the column — which the drawer
                           separates rather than this cell guessing. */
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-2xs text-slate-400">
                      {formatTimestamp(e.last_seen_at)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {openEntry && (
        <BoardDrawer
          entry={openEntry}
          observations={history[openEntry.target_key]}
          note={note}
          championsNote={championsNote}
          reports={reports}
          retire={retire}
          accountEmail={accountEmail}
          getToken={getToken}
          onClose={() => setOpenKey(null)}
        />
      )}
    </>
  )
}

/**
 * One board entry's detail: the mechanism, the Elo range, the observation history,
 * the ligand starting points, the champions fields and the caveats.
 *
 * `eyebrow="Board entry"` is load-bearing. This panel looks exactly like the run
 * tab's, and everything in it is a **consolidation across runs** rather than one
 * run's result — the eyebrow is what stops a reader taking the Elo range for a
 * single tournament's spread.
 *
 * The observation log is the audit trail behind `runs_identified`: a consolidated
 * count that cannot be traced to the rows behind it is not evidence of anything.
 */
function BoardDrawer({
  entry: e,
  observations,
  note,
  championsNote,
  reports,
  retire,
  accountEmail,
  getToken,
  onClose,
}: {
  entry: BoardEntry
  observations: BoardObservation[] | undefined
  note: string | null
  championsNote: string | null
  reports: ReportsState
  retire: BoardRetire
  accountEmail: string
  getToken: TokenGetter
  onClose: () => void
}) {
  // A string on some rows and a number on others: `btl_score` is `numeric`, which
  // psycopg hands over as Decimal and the API stringifies to keep the precision.
  const btlRaw = e.latest_btl
  const btl = btlRaw === null || btlRaw === undefined ? null : Number(btlRaw)

  return (
    <Drawer
      eyebrow="Board entry"
      title={e.protein}
      subtitle={
        <span className="font-mono">
          {e.accession ?? 'no accession on the record'}
        </span>
      }
      onClose={onClose}
      footer={
        observations === undefined ? (
          <p className="text-2xs text-slate-500">Loading this target’s runs…</p>
        ) : (
          <div className="space-y-3">
            <BoardReport
              entry={e}
              observations={observations}
              reports={reports}
              accountEmail={accountEmail}
            />
            <RetireButton
              targetKey={e.target_key}
              protein={e.protein}
              runId={observations[0]?.run_id ?? null}
              retiring={retire.retiring}
              error={retire.retireError}
              onRetireTarget={(reason) => retire.retire(e.target_key, reason)}
              onRetireRun={(reason) =>
                retire.retireRunRows(observations[0]?.run_id ?? '', reason)
              }
            />
          </div>
        )
      }
    >
      <DrawerSection label="Mechanism">
        <p className="text-sm text-slate-200">{e.moa ?? 'No mechanism recorded.'}</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {e.pathway ? (
            <Chip>{e.pathway}</Chip>
          ) : (
            <span className="text-2xs text-slate-600">No pathway recorded.</span>
          )}
        </div>
        {/* Every spelling any run used. The key is what they agreed on, not what any
            one of them wrote, so a reader who knows the protein by another name needs
            this to recognise the row at all. */}
        {e.aliases && e.aliases.length > 1 && (
          <p className="mt-2 text-xs text-slate-400">
            Also called {e.aliases.join(', ')}
          </p>
        )}
      </DrawerSection>

      <DrawerSection label="Latest rationale">
        {/* whitespace-pre-wrap: the agent writes multi-paragraph rationales and
            collapsing them loses the structure. This is the prose the drawer exists
            for — it was being laid out inside a table cell. */}
        <p className="max-w-prose whitespace-pre-wrap text-sm leading-relaxed text-slate-300">
          {e.rationale ?? 'No rationale recorded.'}
        </p>
        <p className="mt-1.5 text-2xs text-slate-500">
          Written by the run that ranked this target last — not a synthesis of what
          every run said. The observations below hold each run’s own.
        </p>
      </DrawerSection>

      <DrawerSection label="Score and coverage">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="w-12 shrink-0 text-2xs text-slate-500">latest</span>
            <ScoreMeter score={e.latest_score} coverage={e.latest_coverage} />
          </div>
          <div className="flex items-center gap-3">
            <span className="w-12 shrink-0 text-2xs text-slate-500">best</span>
            <ScoreMeter score={e.best_score} coverage={e.best_coverage} />
          </div>
          {/* Said rather than implied, and only where there is a figure to qualify:
              `ScoreMeter` prints its two arguments as a pair, and on the `best` row
              they are independent maxima — the best score any run reached and the
              best coverage any run reached, which
              `infra/sql/012_score_and_coverage.sql` says need not be the same run.
              The `latest` row above IS one run's pair. */}
          {(e.best_score ?? e.best_coverage) !== null &&
            (e.best_score ?? e.best_coverage) !== undefined && (
              <p className="pl-[3.75rem] text-2xs text-slate-500">
                Each best figure is the highest any run reached, taken independently,
                so the two may come from different runs. The observations below hold
                each run’s own pair.
              </p>
            )}
          {/* The champions recheck's pair (`infra/sql/022`), shown here whether or not
              a run also scored the row — the drawer is where the two are compared, and
              a reader who cannot see both cannot tell agreement from absence. The cell
              in the table shows it only where nothing measured exists.

              `ScoreMeter` is the right component and not a hazard: 022 takes the score
              and the coverage from ONE champions row, so this is one observation's pair,
              unlike `best` above. What keeps it from reading as a measurement is the
              gutter label and the sentence under it, which name the source. */}
          {e.champion_score != null && (
            <>
              <div className="flex items-center gap-3">
                <span className="w-12 shrink-0 text-2xs text-amber-400">recheck</span>
                <ScoreMeter score={e.champion_score} coverage={e.champion_coverage} />
              </div>
              <p className="pl-[3.75rem] text-2xs leading-relaxed text-slate-500">
                {e.champion_score_source === 'run' ? (
                  <>
                    Re-scored by a champions session from axis scores a review
                    measured, under that session’s rubric weights. Arithmetic over
                    stored measurements — no run’s ranking holds this number, because
                    the run that measured the axes stored no score.
                  </>
                ) : (
                  <>
                    <span className="text-amber-400">
                      Re-derived, not measured.
                    </span>{' '}
                    No axis scores were stored for this target, so a champions session
                    gave the model this record and asked it to reconstruct them. The
                    number is inferred from the rationale by the same model that wrote
                    it: a reading aid for a row nobody scored, and not evidence that
                    anyone scored it.
                  </>
                )}{' '}
                Session{' '}
                <span className="font-mono">
                  {e.champion_score_session ?? 'not recorded'}
                </span>
                , source{' '}
                <span className="font-mono">
                  {e.champion_score_source ?? 'not recorded'}
                </span>
                .
              </p>
            </>
          )}
          <div className="flex items-center gap-3">
            <span className="w-12 shrink-0 text-2xs text-slate-500">coverage</span>
            {/* Coverage on its own bar, because it is what stands in for the axis
                spine here. Stored 0–1, and `Meter` bars 0–100. */}
            <Meter
              value={
                e.latest_coverage === null || e.latest_coverage === undefined
                  ? e.latest_coverage
                  : e.latest_coverage * 100
              }
              label="share of the rubric measured, latest run"
            >
              {/* The unit, and only when there is a figure to qualify. `Meter` renders
                  its children beside either branch, so an unconditional suffix reads
                  "Not measured % of the rubric's weight measured" on an unscored row —
                  the suffix belongs to the number, not to the absence of one. */}
              {e.latest_coverage !== null && e.latest_coverage !== undefined && (
                <span className="text-2xs text-slate-500">
                  % of the rubric’s weight measured
                </span>
              )}
            </Meter>
          </div>
        </div>
        <p className="mt-2 text-2xs leading-relaxed text-slate-500">
          There is no per-axis breakdown at this level: a board row is a
          consolidation and carries no <span className="font-mono">axis_scores</span>{' '}
          — the store projects the score and its coverage only. Which axes went
          unmeasured is a property of one run’s review, so it is on that run’s row in
          “This run”. Splitting a single coverage figure into axes here would invent
          the breakdown.
        </p>
      </DrawerSection>

      <DrawerSection label="Elo across the runs that ranked it">
        <EloRange
          className="max-w-xs"
          worst={e.worst_elo}
          latest={e.latest_elo}
          best={e.best_elo}
        />
        <p className="mt-2 text-2xs leading-relaxed text-slate-500">
          Unbounded, base 1500, K=24 — a range rather than a fraction of anything.
          {btl !== null && !Number.isNaN(btl) && (
            <>
              {' '}
              The latest run’s BTL strength was{' '}
              <span className="font-mono tabular-nums text-slate-400">
                {btl.toFixed(4)}
              </span>
              , a probability within that run’s pool and not across the board.
            </>
          )}
        </p>
      </DrawerSection>

      <DrawerSection label="Observations">
        {observations === undefined ? (
          <p className="text-2xs text-slate-500">Loading…</p>
        ) : observations.length === 0 ? (
          <p className="text-2xs text-slate-500">
            The API returned no per-run rows for this target.
          </p>
        ) : (
          <>
            <EloStrip observations={observations} />
            <ul className="mt-3 space-y-1.5">
              {observations.map((o) => (
                <li
                  key={`${o.run_id}-${o.created_at ?? ''}`}
                  className="flex flex-wrap items-baseline gap-2"
                >
                  <span className="font-mono text-2xs text-slate-400">
                    {o.run_id}
                  </span>
                  <TierBadge tier={o.tier} />
                  <span className="font-mono text-2xs tabular-nums text-slate-300">
                    #{o.rank ?? '—'} · Elo {formatNumber(o.elo)}
                  </span>
                  <span className="text-2xs text-slate-500">
                    {formatTimestamp(o.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </DrawerSection>

      <LigandStarts entry={e} getToken={getToken} />

      <DrawerSection label="Champions">
        {e.champions_rounds === undefined ? (
          /* Three states again, and the first two are not the same: no column at all
             on the runtime that served this row, versus a column saying it never
             entered a tournament. */
          <p className="text-2xs text-slate-500">
            This response carried no champions fields — the runtime that served it
            predates the champions log.
          </p>
        ) : e.champions_rounds === 0 ? (
          <p className="text-2xs text-slate-500">
            Has not entered a champions tournament. Not the same as placing last in
            one.
            {/* And not the same as never having been looked at, which is why the
                score section above can hold a figure while this one is empty: the
                recheck covers every scanned row and the pool is cut afterwards at
                the tier floor. */}
            {e.champion_score != null && (
              <>
                {' '}
                A session did recheck its tier — see “Score and coverage” above — and
                then left it out of the pool.
              </>
            )}
          </p>
        ) : (
          <div className="space-y-1.5 text-xs text-slate-300">
            <p>
              <span className="font-mono tabular-nums text-slate-100">
                {formatNumber(e.champion_elo)}
              </span>{' '}
              {e.champion_rank != null && <>at rank #{e.champion_rank} </>}in{' '}
              <span className="font-mono">{e.champion_session ?? 'an unnamed session'}</span>
              , after {e.champions_rounds} tournament
              {e.champions_rounds === 1 ? '' : 's'} entered — entries, not wins.
            </p>
            {e.champion_rationale && (
              <p className="whitespace-pre-wrap leading-relaxed text-slate-400">
                {e.champion_rationale}
              </p>
            )}
            {/* Beside the run's own rationale, never in place of it: a re-wording of
                stored evidence is not what a run recorded. */}
            {championsNote && (
              <p className="text-2xs leading-relaxed text-slate-500">{championsNote}</p>
            )}
          </div>
        )}
      </DrawerSection>

      <DrawerSection label="Recap">
        {e.was_disqualified === true ? (
          <p className="text-xs leading-relaxed text-slate-300">
            Ruled out by at least one run
            {e.disqualified_in_runs?.length ? (
              <>
                {' '}
                (<span className="font-mono">{e.disqualified_in_runs.join(', ')}</span>)
              </>
            ) : null}
            . It is back on the board because a recap re-reviewed it and something had
            measurably changed — the dead-end row still stands.
          </p>
        ) : e.was_disqualified === false ? (
          <p className="text-xs leading-relaxed text-slate-300">
            A recap examined this target and found no run that ruled it out.
          </p>
        ) : (
          <p className="text-xs leading-relaxed text-slate-400">
            No recap has examined this target. That is not a clean bill of health:
            nothing has looked, so there is no verdict either way.
          </p>
        )}
      </DrawerSection>

      <DrawerSection label="Evidence">
        <EvidenceLinks evidence={e.evidence} />
      </DrawerSection>

      <DrawerSection label="Provenance">
        <dl className="space-y-2 text-xs">
          <div>
            <dt className="text-2xs text-slate-500">Target key</dt>
            <dd className="break-all font-mono text-2xs text-slate-400">
              {e.target_key}
            </dd>
          </div>
          <div>
            <dt className="text-2xs text-slate-500">First seen</dt>
            <dd className="text-slate-300">
              <span className="font-mono">{e.first_seen_run ?? '—'}</span>{' '}
              {formatTimestamp(e.first_seen_at)}
            </dd>
          </div>
          <div>
            <dt className="text-2xs text-slate-500">Last seen</dt>
            <dd className="text-slate-300">
              <span className="font-mono">{e.last_seen_run ?? '—'}</span>{' '}
              {formatTimestamp(e.last_seen_at)}
            </dd>
          </div>
          <div>
            {/* The audit trail behind the count. A consolidated number that cannot be
                traced to the rows behind it is not evidence of anything. */}
            <dt className="text-2xs text-slate-500">
              Runs behind this row ({e.runs_identified}; {e.distinct_prompts} prompt
              {e.distinct_prompts === 1 ? '' : 's'}, {e.distinct_threads} thread
              {e.distinct_threads === 1 ? '' : 's'})
            </dt>
            <dd className="mt-0.5 flex flex-wrap gap-1">
              {(e.run_ids ?? []).map((id) => (
                <Chip key={id} font="mono">
                  {id}
                </Chip>
              ))}
            </dd>
          </div>
        </dl>
        {/* The server's sentence again, in the panel that shows the count again. It
            travels with the number on every read rather than being stated once at the
            top of the page a reader has scrolled past. */}
        {note && (
          <p className="mt-3 text-2xs leading-relaxed text-slate-400">{note}</p>
        )}
        <p className="mt-2 text-2xs leading-relaxed text-slate-500">
          Board rows are compared with nothing: each figure above came from the run
          that produced it, and two rows on this board can come from two different
          sweeps.
        </p>
      </DrawerSection>
    </Drawer>
  )
}

/**
 * The ligand starting points behind the count.
 *
 * The column can only carry a number; this is where the things a chemist orders
 * live, and they are rendered as text so they can be copied. Two rules the layout
 * enforces rather than mentions:
 *
 * * A `homolog` entry shows its organism, identity and coverage **on the same line
 *   as the PDB id**. A structure of a bacterial or human relative does not establish
 *   this protein's pocket, and a bare four-character code beside the target's name
 *   is how that claim gets made without anybody deciding to make it.
 * * A catalog hit shows source, id and similarity together. The tier alone is a band
 *   and the id alone is unverifiable, and the id is what gets typed into an order.
 */
function LigandStarts({ entry, getToken }: { entry: BoardEntry; getToken: TokenGetter }) {
  const starts = entry.latest_ligand_starts
  const source = entry.ligand_start_source ?? null
  // Nothing to say and no gap to name: the row predates the lookup, which the
  // column already renders as "not measured".
  if (source == null && starts == null) {
    return (
      <DrawerSection label="Ligand starting points">
        <p className="text-2xs leading-relaxed text-slate-500">
          This board row predates the ligand lookup. Not the same as no orderable
          compound existing — nothing has looked.
        </p>
      </DrawerSection>
    )
  }
  return (
    <DrawerSection label="Ligand starting points">
      <p className="text-2xs leading-relaxed text-slate-500">
        Recorded, not scored — nothing here moved this target’s rank. A catalog hit
        means a similar molecule can be bought, not that it is active.
        {entry.ligand_starts_checked_at && (
          <> Looked up {formatTimestamp(entry.ligand_starts_checked_at)}.</>
        )}
      </p>
      {source && source !== 'measured' && (
        <p className="mt-2 text-2xs text-slate-400">
          <span className="font-mono">{source}</span>
          {' — '}
          {LIGAND_SOURCE_NOTES[source] ?? 'No description for this source value.'}
        </p>
      )}
      {starts && starts.length > 0 ? (
        // One table, scrolled sideways rather than wrapped: a hit is read across a row
        // (where it came from → what it is → how close), and wrapping the SMILES
        // column broke that line into the next hit's.
        <div className="mt-2 overflow-x-auto rounded border border-slate-800">
          <table className="min-w-max text-left text-2xs">
            <thead className="bg-slate-900/60 text-slate-400">
              <tr>
                {['Structure', 'Ligand', 'Source', 'Compound', 'SMILES', 'Similarity', 'Tier'].map(
                  (h) => (
                    <th key={h} className="whitespace-nowrap px-2 py-1 font-medium">
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {starts.map((s) => {
                const hits = s.catalog_hits ?? []
                const span = Math.max(hits.length, 1)
                const structure = (
                  <td rowSpan={span} className="whitespace-nowrap px-2 py-1 align-top">
                    <a
                      href={`https://www.rcsb.org/structure/${s.pdb_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-brand-300 hover:underline"
                    >
                      {s.pdb_id}
                    </a>
                    <div>
                      {s.structure_provenance === 'homolog' ? (
                        <span
                          className="rounded bg-amber-900/40 px-1.5 py-0.5 text-amber-300"
                          title="A structure of a different protein. It says the family binds this chemistry; it does not establish this protein's pocket."
                        >
                          homolog · {s.structure_organism ?? 'organism not named'} ·{' '}
                          {Math.round(s.structure_identity * 100)}% id over{' '}
                          {Math.round(s.structure_coverage * 100)}%
                        </span>
                      ) : (
                        <span className="text-slate-500">this protein’s structure</span>
                      )}
                    </div>
                  </td>
                )
                const ligand = (
                  <td rowSpan={span} className="max-w-[16rem] px-2 py-1 align-top text-slate-300">
                    <span className="font-mono text-slate-200">{s.comp_id}</span>
                    {s.ligand_name ? ` — ${s.ligand_name}` : ''}
                    {s.kept_because === 'drug_like' && (
                      <span
                        className="ml-1 text-slate-500"
                        title="Kept by a heavy-atom heuristic, not by RCSB's curated subject-of-investigation annotation. It may be a large buffer component."
                      >
                        (heuristic)
                      </span>
                    )}
                  </td>
                )
                if (hits.length === 0) {
                  return (
                    <tr key={`${s.pdb_id}-${s.comp_id}`} className="border-t border-slate-800">
                      {structure}
                      {ligand}
                      <td colSpan={5} className="px-2 py-1 text-slate-500">
                        {s.catalog_hits == null
                          ? 'Catalog not searched for this ligand.'
                          : 'No similar compound in the catalogs searched.'}
                      </td>
                    </tr>
                  )
                }
                return hits.map((h, i) => (
                  <tr
                    key={`${s.pdb_id}-${s.comp_id}-${h.source}-${h.compound_id}`}
                    className={i === 0 ? 'border-t border-slate-800' : undefined}
                  >
                    {i === 0 && structure}
                    {i === 0 && ligand}
                    <td className="whitespace-nowrap px-2 py-1 text-slate-400">{h.source ?? '?'}</td>
                    <td className="whitespace-nowrap px-2 py-1">
                      <CompoundId hit={h} smilesColumn />
                    </td>
                    <td className="whitespace-nowrap px-2 py-1 font-mono text-slate-300">
                      {h.smiles ?? <span className="text-slate-500">not captured</span>}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1 font-mono text-slate-400">
                      {formatNumber(h.similarity, 2)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1 text-blue-400/80">{h.tier}</td>
                  </tr>
                ))
              })}
            </tbody>
          </table>
        </div>
      ) : (
        starts != null && (
          <p className="mt-2 text-2xs text-slate-500">
            Searched; no ligand worth ordering an analog of. A result, not a gap.
          </p>
        )
      )}
      <OrderCompounds entry={entry} getToken={getToken} />
    </DrawerSection>
  )
}

/**
 * The report affordance on a board row, in the drawer's footer.
 *
 * A board entry is a consolidation, and a report is an assessment of ONE run's
 * hypothesis — its rationale, its review verdicts, its Elo history. So this offers
 * a report on the **newest observation**: the most recent run that ranked the
 * target, named in the confirmation so nobody thinks they asked for a synthesis of
 * all of them.
 *
 * Its own component for the effect. The reports hook only lists runs it has been
 * told about, and this run is generally not the one selected in the sidebar, so
 * opening the drawer has to register it — otherwise a report requested here would
 * sit at `queued` forever because nothing polls its run.
 */
function BoardReport({
  entry,
  observations,
  reports,
  accountEmail,
}: {
  entry: BoardEntry
  observations: BoardObservation[]
  reports: ReportsState
  accountEmail: string
}) {
  // Newest first, per `list_board_history`'s `ORDER BY created_at DESC`.
  const latest = observations[0]
  const runId = latest?.run_id ?? null
  const { track } = reports

  useEffect(() => {
    if (runId) track(runId)
  }, [runId, track])

  if (!latest) return null

  // Rows written before the store projected `target_id` — and any row this UI is
  // newer than the API for. Saying so beats an enabled button that would POST to
  // `/targets/undefined/report`.
  if (typeof latest.target_id !== 'number') {
    return (
      <p className="text-2xs leading-relaxed text-slate-500">
        A full report is not available for this entry: the API did not return the
        per-run row id it needs. Open the run in “This run” and request it there.
      </p>
    )
  }

  const report = reports.byTarget.get(latest.target_id)
  return (
    <div className="space-y-1.5">
      <p className="text-2xs text-slate-500">
        A report assesses one run’s hypothesis. This one is filed against{' '}
        <span className="font-mono">{latest.run_id}</span>, the newest run that ranked
        this target — not a synthesis across runs.
      </p>
      <ReportButton
        targetId={latest.target_id}
        protein={entry.protein}
        accession={entry.accession}
        runId={latest.run_id}
        runLabel={latest.run_id}
        report={report}
        accountEmail={accountEmail}
        requesting={reports.requesting === latest.target_id}
        downloading={reports.downloading !== null && reports.downloading === report?.id}
        error={
          reports.error?.targetId === latest.target_id ? reports.error.message : null
        }
        onRequest={reports.request}
        onDownload={reports.download}
      />
    </div>
  )
}
